/**
 * VoiceInput -- Web Speech API voice input for chat.
 *
 * Provides speech-to-text using the SpeechRecognition API.
 * Supports: ar, en, fr, es, de.
 * Falls back gracefully on unsupported browsers with a "coming soon" callback.
 */

// Extend Window for vendor-prefixed SpeechRecognition
interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
  resultIndex: number;
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string;
  message: string;
}

interface SpeechRecognitionInstance extends EventTarget {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  onstart: (() => void) | null;
}

declare global {
  interface Window {
    SpeechRecognition?: new () => SpeechRecognitionInstance;
    webkitSpeechRecognition?: new () => SpeechRecognitionInstance;
  }
}

const LOCALE_MAP: Record<string, string> = {
  en: 'en-US',
  fr: 'fr-FR',
  ar: 'ar-SA',
  es: 'es-ES',
  de: 'de-DE',
};

export default class VoiceInput {
  private recognition: SpeechRecognitionInstance | null = null;
  private isListening = false;
  private onResult: ((text: string) => void) | null = null;
  private onUnsupported: (() => void) | null = null;

  /**
   * Check if Web Speech API is available.
   */
  static isSupported(): boolean {
    return !!(window.SpeechRecognition || window.webkitSpeechRecognition);
  }

  /**
   * Start listening for voice input.
   *
   * @param locale - Language code (en, fr, ar, es, de)
   * @param onResult - Callback with transcribed text
   * @param onUnsupported - Callback if speech recognition not available
   */
  startListening(
    locale: string,
    onResult: (text: string) => void,
    onUnsupported: () => void,
  ): void {
    this.onResult = onResult;
    this.onUnsupported = onUnsupported;

    if (!VoiceInput.isSupported()) {
      onUnsupported();
      return;
    }

    const SpeechRec = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRec) {
      onUnsupported();
      return;
    }

    this.recognition = new SpeechRec();
    this.recognition.lang = LOCALE_MAP[locale] || LOCALE_MAP.en;
    this.recognition.continuous = false;
    this.recognition.interimResults = false;
    this.recognition.maxAlternatives = 1;

    this.recognition.onstart = () => {
      this.isListening = true;
      this._showRecordingUI();
    };

    this.recognition.onresult = (event: SpeechRecognitionEvent) => {
      const transcript = event.results[event.resultIndex][0].transcript;
      if (transcript && this.onResult) {
        this.onResult(transcript.trim());
      }
    };

    this.recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      console.warn('[VoiceInput] Error:', event.error);
      this.isListening = false;
      this._hideRecordingUI();

      if (event.error === 'not-allowed' || event.error === 'service-not-available') {
        if (this.onUnsupported) this.onUnsupported();
      }
    };

    this.recognition.onend = () => {
      this.isListening = false;
      this._hideRecordingUI();
    };

    try {
      this.recognition.start();
    } catch {
      onUnsupported();
    }
  }

  /**
   * Stop listening.
   */
  stopListening(): void {
    if (this.recognition && this.isListening) {
      this.recognition.stop();
    }
    this.isListening = false;
    this._hideRecordingUI();
  }

  /**
   * Show a pulsing red recording indicator overlay.
   */
  private _showRecordingUI(): void {
    if (document.querySelector('#aweni-voice-overlay')) return;

    const overlay = document.createElement('div');
    overlay.id = 'aweni-voice-overlay';
    overlay.style.cssText = `
      position: fixed;
      bottom: 80px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 100;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;
      animation: voice-fade-in var(--motion-duration-base) var(--motion-easing-default);
    `;

    overlay.innerHTML = `
      <div style="
        width: 64px; height: 64px; border-radius: 50%;
        background: #EF4444;
        display: flex; align-items: center; justify-content: center;
        position: relative;
        cursor: pointer;
      " id="aweni-voice-stop">
        <span aria-hidden="true" style="
          position: absolute;
          inset: -6px;
          border-radius: 999px;
          border: 2px solid rgba(239, 68, 68, 0.55);
          transform-origin: center;
          animation: voice-pulse-ring var(--motion-duration-deliberate) var(--motion-easing-emphasized) infinite;
          pointer-events: none;
        "></span>
        <svg width="28" height="28" viewBox="0 0 24 24" fill="white" stroke="none" style="position:relative;z-index:1;">
          <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
          <path d="M19 10v2a7 7 0 0 1-14 0v-2" fill="none" stroke="white" stroke-width="2"/>
        </svg>
      </div>
      <span style="
        font-size: 12px; font-weight: 600; color: var(--text-primary);
        padding: 4px 12px; border-radius: 12px;
        background: var(--card-bg); box-shadow: var(--shadow-sm);
      ">Listening...</span>
    `;

    // Add animations
    if (!document.querySelector('#aweni-voice-styles')) {
      const style = document.createElement('style');
      style.id = 'aweni-voice-styles';
      style.textContent = `
        @keyframes voice-pulse-ring {
          0% { transform: scale(0.82); opacity: 0.72; }
          70% { transform: scale(1.34); opacity: 0; }
          100% { transform: scale(1.34); opacity: 0; }
        }
        @keyframes voice-fade-in {
          from { opacity: 0; transform: translateX(-50%) translateY(10px); }
          to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `;
      document.head.appendChild(style);
    }

    // Stop on click
    overlay.querySelector('#aweni-voice-stop')?.addEventListener('click', () => {
      this.stopListening();
    });

    document.body.appendChild(overlay);
  }

  /**
   * Remove the recording indicator overlay.
   */
  private _hideRecordingUI(): void {
    const overlay = document.querySelector('#aweni-voice-overlay');
    if (overlay) {
      overlay.remove();
    }
  }
}
